import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  CircularProgress,
  Snackbar,
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

const UpdatePassword = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const formData = {
      oldPassword: data.get('oldPassword'),
      newPassword: data.get('newPassword'),
      confirmPassword: data.get('confirmPassword'),
    };

    // Validate form
    const errors = {};
    if (!formData.oldPassword)
      errors.oldPassword = 'Current password is required';
    if (!formData.newPassword) errors.newPassword = 'New password is required';
    if (!formData.confirmPassword)
      errors.confirmPassword = 'Confirm password is required';
    if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('Not authenticated');
        navigate('/');
        return;
      }

      // Decode token to get adminId
      const decodedToken = jwtDecode(token);
      const adminId = decodedToken.adminId;
      const response = await fetch(
        `https://webback.opencurtainscasting.com/admin/changePassword/${adminId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        setSnackbarMessage('Password updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update password');
      }
    } catch (error) {
      setError('An error occurred while updating password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        maxHeight: '600px',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#6e0506',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper elevation={6} sx={{ p: 4, maxWidth: '500px', width: '100%' }}>
          <Typography variant='h4' textAlign='center' mb={4}>
            Update Password
          </Typography>

          <Box component='form' onSubmit={handleSubmit}>
            <TextField
              margin='normal'
              required
              fullWidth
              name='oldPassword'
              label='Current Password'
              type={showOldPassword ? 'text' : 'password'}
              error={!!formErrors.oldPassword}
              helperText={formErrors.oldPassword}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#6e0506',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#6e0506',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() => setShowOldPassword(!showOldPassword)}
                    >
                      {showOldPassword ? (
                        <Visibility sx={{ color: '#6e0506' }} />
                      ) : (
                        <VisibilityOff sx={{ color: '#6e0506' }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin='normal'
              required
              fullWidth
              name='newPassword'
              label='New Password'
              type={showNewPassword ? 'text' : 'password'}
              error={!!formErrors.newPassword}
              helperText={formErrors.newPassword}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#6e0506',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#6e0506',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <Visibility sx={{ color: '#6e0506' }} />
                      ) : (
                        <VisibilityOff sx={{ color: '#6e0506' }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin='normal'
              required
              fullWidth
              name='confirmPassword'
              label='Confirm New Password'
              type={showConfirmPassword ? 'text' : 'password'}
              error={!!formErrors.confirmPassword}
              helperText={formErrors.confirmPassword}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#6e0506',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#6e0506',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <Visibility sx={{ color: '#6e0506' }} />
                      ) : (
                        <VisibilityOff sx={{ color: '#6e0506' }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
              <Typography color='error' sx={{ mt: 2 }} textAlign='center'>
                {error}
              </Typography>
            )}

            <Button
              type='submit'
              fullWidth
              variant='contained'
              sx={{
                mt: 3,
                mb: 2,
                height: 56,
                bgcolor: '#6e0506',
                '&:hover': {
                  backgroundColor: '#941416',
                },
              }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Update Password'}
            </Button>
          </Box>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={2000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MuiAlert
              elevation={6}
              variant='filled'
              severity={snackbarSeverity}
              onClose={() => setSnackbarOpen(false)}
            >
              {snackbarMessage}
            </MuiAlert>
          </Snackbar>
        </Paper>
      </Box>
    </Box>
  );
};

export default UpdatePassword;
